import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzTableModule } from 'ng-zorro-antd/table';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { FlexModule } from '@angular/flex-layout';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { MatIconModule } from '@angular/material/icon';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { TooltipComponent } from 'src/app/common/tooltip/tooltip.component';
import { ILaboratoryFileDetailed } from 'src/app/interfaces/LaboratoryFile/ILaboratoryFileDetailed';
import { LaboratoryFileService } from 'src/app/services/laboratoryFile.service';
import { Subject, takeUntil } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import Swal from 'sweetalert2';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LaboratoryFileAddComponent } from './laboratory-file-add/laboratory-file-add.component';
import moment from 'moment';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { PermissionModule } from 'src/app/modules/permission.module';
import { NzImageModule } from 'ng-zorro-antd/image';
import { ALLOWED_MIME } from 'src/app/guards/file.guard';

@Component({
  selector: 'app-laboratory-file',
  standalone: true,
  templateUrl: './laboratory-file.component.html',
  styleUrls: ['./laboratory-file.component.css'],
  imports: [
    NzLayoutModule,
    NzTableModule,
    TranslateModule,
    CommonModule,
    FlexModule,
    NzTooltipModule,
    NzIconModule,
    MatIconModule,
    TooltipComponent,
    NzSwitchModule,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzPaginationModule,
    BrowserModule,
    PdfViewerModule,
    NzSpinModule,
    NzImageModule,
    PermissionModule,
  ],
})
export class LaboratoryFileComponent {
  @Input() isPreview: boolean = false;
  @Input() isLoading?: boolean = false;
  isPreviewLoading?: boolean = false;

  @Input() pagination?: boolean = true;

  public addComp = LaboratoryFileAddComponent;

  private destroy$ = new Subject<void>();

  pdfObj?: string;
  pdfItem?: ILaboratoryFileDetailed;

  @Input() data?: ILaboratoryFileDetailed[];

  currentPageIndex: number = 1;
  pageSize: number = 5;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal
  isVisible = false;

  fileType?: string;
  fileName?: string;

  constructor(
    private readonly laboratoryService: LaboratoryFileService,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly swalToastService: SwalToastService,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.laboratoryService.laboratoryLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    if (!this.data) {
      this.laboratoryService.laboratoryDataSub
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: ILaboratoryFileDetailed[]) => {
          this.data = data;

          if (data.at(0)) {
            this.requestPDF(data.at(0));
          }
        });

      this.laboratoryService.getData();
    }
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  hasUnsubmitted() {
    return this.getData()?.find(
      (d) => d?.['isSubmitted'] !== undefined && !d?.['isSubmitted']
    );
  }

  getData() {
    if (this.pagination) {
      const startIndex = (this.currentPageIndex - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this?.data?.slice(startIndex, endIndex) || [];
    }
    return this?.data;
  }

  handlePageIndexChange(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
  }

  handlePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPageIndex = 1; // Reset to the first page when changing page size
  }

  openModal(item?: ILaboratoryFileDetailed) {
    this.laboratoryService.laboratoryModalSub.next(true);
    this.laboratoryService.laboratoryEditSub.next(item);
  }

  deleteFun(id: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder3'
      )} ${this.translate.instant('Laboratory')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.laboratoryService.deleteLaboratory(id.toString()).subscribe({
          next: (d) => {
            this.swalToastService.toastSuccess(
              'Laboratory deleted successfully'
            );
            if (id == this?.pdfItem?.id) {
              this.pdfObj = undefined;
              this.pdfItem = undefined;
            }

            this.laboratoryService.deleteLaboratoryFromTable(id.toString());
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to delete Laboratory');
          },
        });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Laboratory is not deleted')}`,
          '',
          'info'
        );
      }
    });
  }
  requestPDF(item?: ILaboratoryFileDetailed) {
    this.isPreviewLoading = true;
    this.laboratoryService.getPDF(item?.id?.toString()!).subscribe({
      next: (res: Blob) => {
        if (!ALLOWED_MIME.includes(res.type as any)) {
          this.swalToastService.toastError('Unsupported file type.');
          this.isPreviewLoading = false;
          this.pdfObj = undefined;
          this.pdfItem = undefined;
          this.fileType = undefined;
          this.fileName = undefined;
          return;
        }
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = URL.createObjectURL(res);
        this.fileType = res.type || undefined;
        this.pdfItem = item;
        const filePath = item?.path || '';
        const fileNameIndex = filePath.lastIndexOf('/');
        this.fileName =
          fileNameIndex === -1
            ? item?.examTitle
            : filePath.substring(fileNameIndex + 1);
        this.isPreviewLoading = false;
      },
      error: () => {
        this.swalToastService.toastError('Unable to retrieve the document');
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = undefined;
        this.pdfItem = undefined;
        this.fileType = undefined;
        this.fileName = undefined;
        this.isPreviewLoading = false;
      },
    });
  }

  downloadPDF(item: ILaboratoryFileDetailed) {
    this.laboratoryService.downloadPDF(item);
  }

  getValue(field: string, value?: string) {
    if (!value) {
      return '-';
    }

    if (field?.toLowerCase()?.includes('date time')) {
      return moment(value).format(datetime_format);
    } else if (field?.toLowerCase()?.includes('date')) {
      return moment(value).format(date_format);
    } else {
      return value;
    }
  }

  handleCancel() {
    this.isVisible = false;

    this.modalRef.destroy();
  }

  hasPermission(permission: string[]) {
    return this.permissionService.can(permission);
  }
}
